import React, { useState, useEffect } from 'react';
import { projectFirestore } from '../../firebase/config';
import { CLOUDINARY_CLOUD_NAME, CLOUDINARY_UPLOAD_PRESET } from '../../cloudinary/config';

const DEFAULT_CLOUDINARY = {
    id: 'default',
    label: 'الحساب الأساسي',
    cloudName: CLOUDINARY_CLOUD_NAME,
    preset: CLOUDINARY_UPLOAD_PRESET,
    enabled: true,
};

const SourceManager = () => {
    const [activeSource, setActiveSource] = useState('cloudinary');
    const [cloudinaryAccounts, setCloudinaryAccounts] = useState([]);
    const [firebaseAccounts, setFirebaseAccounts] = useState([]);
    const [activeCloudinary, setActiveCloudinary] = useState('');
    const [activeFirebase, setActiveFirebase] = useState('');
    const [initialized, setInitialized] = useState(false);

    // حقول إضافة حساب جديد
    const [newCloudName, setNewCloudName] = useState('');
    const [newCloudPreset, setNewCloudPreset] = useState('');
    const [newCloudLabel, setNewCloudLabel] = useState('');
    const [newFbLabel, setNewFbLabel] = useState('');
    const [newFbConfig, setNewFbConfig] = useState('');

    useEffect(() => {
        const unsub = projectFirestore.collection('settings').doc('imageSource')
            .onSnapshot(async (doc) => {
                if (doc.exists) {
                    const data = doc.data();
                    setActiveSource(data.activeSource || 'cloudinary');
                    // ضمان وجود حقل enabled في كل الحسابات القديمة
                    const cAccounts = (data.cloudinaryAccounts || []).map(a => ({ ...a, enabled: a.enabled !== false }));
                    const fAccounts = (data.firebaseAccounts || []).map(a => ({ ...a, enabled: a.enabled !== false }));
                    setCloudinaryAccounts(cAccounts);
                    setFirebaseAccounts(fAccounts);
                    setActiveCloudinary(data.activeCloudinary || '');
                    setActiveFirebase(data.activeFirebase || '');
                } else {
                    // لا يوجد إعدادات — إنشاء تلقائي بالحساب الأساسي
                    const initial = {
                        activeSource: 'cloudinary',
                        cloudinaryAccounts: [DEFAULT_CLOUDINARY],
                        firebaseAccounts: [],
                        activeCloudinary: 'default',
                        activeFirebase: '',
                    };
                    await projectFirestore.collection('settings').doc('imageSource').set(initial);
                }
                setInitialized(true);
            });
        return () => unsub();
    }, []);

    const saveSettings = async (updates) => {
        await projectFirestore.collection('settings').doc('imageSource').set(updates, { merge: true });
    };

    const handleSourceChange = async (source) => {
        await saveSettings({ activeSource: source });
    };

    const handleAddCloudinary = async () => {
        if (!newCloudName || !newCloudPreset) { alert('أدخل Cloud Name و Upload Preset'); return; }
        const account = {
            id: Date.now().toString(),
            label: newCloudLabel || newCloudName,
            cloudName: newCloudName,
            preset: newCloudPreset,
            enabled: true,
        };
        const updated = [...cloudinaryAccounts, account];
        const updates = { cloudinaryAccounts: updated };
        if (!activeCloudinary) updates.activeCloudinary = account.id;
        await saveSettings(updates);
        setNewCloudName(''); setNewCloudPreset(''); setNewCloudLabel('');
    };

    const handleAddFirebase = async () => {
        if (!newFbConfig) { alert('أدخل إعدادات Firebase (JSON)'); return; }
        let config;
        try { config = JSON.parse(newFbConfig); } catch { alert('صيغة JSON غير صحيحة'); return; }
        const account = {
            id: Date.now().toString(),
            label: newFbLabel || config.projectId || 'Firebase',
            config,
            enabled: true,
        };
        const updated = [...firebaseAccounts, account];
        const updates = { firebaseAccounts: updated };
        if (!activeFirebase) updates.activeFirebase = account.id;
        await saveSettings(updates);
        setNewFbConfig(''); setNewFbLabel('');
    };

    const handleDeleteAccount = async (type, id) => {
        if (!window.confirm('هل تريد حذف هذا الحساب؟')) return;
        if (type === 'cloudinary') {
            const updated = cloudinaryAccounts.filter(a => a.id !== id);
            const updates = { cloudinaryAccounts: updated };
            if (activeCloudinary === id) updates.activeCloudinary = updated[0]?.id || '';
            await saveSettings(updates);
        } else {
            const updated = firebaseAccounts.filter(a => a.id !== id);
            const updates = { firebaseAccounts: updated };
            if (activeFirebase === id) updates.activeFirebase = updated[0]?.id || '';
            await saveSettings(updates);
        }
    };

    const handleActivateAccount = async (type, id) => {
        if (type === 'cloudinary') {
            await saveSettings({ activeCloudinary: id });
        } else {
            await saveSettings({ activeFirebase: id });
        }
    };

    const handleToggleEnabled = async (type, id) => {
        if (type === 'cloudinary') {
            const acc = cloudinaryAccounts.find(a => a.id === id);
            if (!acc) return;
            // لا يمكن تعطيل الحساب النشط
            if (acc.enabled && activeCloudinary === id) {
                alert('لا يمكن تعطيل الحساب النشط. قم بتفعيل حساب آخر أولاً.');
                return;
            }
            const updated = cloudinaryAccounts.map(a => a.id === id ? { ...a, enabled: !a.enabled } : a);
            await saveSettings({ cloudinaryAccounts: updated });
        } else {
            const acc = firebaseAccounts.find(a => a.id === id);
            if (!acc) return;
            if (acc.enabled && activeFirebase === id) {
                alert('لا يمكن تعطيل الحساب النشط. قم بتفعيل حساب آخر أولاً.');
                return;
            }
            const updated = firebaseAccounts.map(a => a.id === id ? { ...a, enabled: !a.enabled } : a);
            await saveSettings({ firebaseAccounts: updated });
        }
    };

    if (!initialized) {
        return <div className="text-center py-4"><div className="spinner-border spinner-border-sm text-primary"></div> جاري التحميل...</div>;
    }

    return (
        <div className="container my-4">
            <h4 className="text-center mb-4">مصادر الصور</h4>

            {/* اختيار المصدر النشط */}
            <div className="card shadow-sm mb-4">
                <div className="card-body">
                    <h5 className="card-title mb-3">المصدر النشط للرفع والعرض</h5>
                    <div className="btn-group w-100" role="group">
                        <button className={`btn ${activeSource === 'cloudinary' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => handleSourceChange('cloudinary')}>
                            ☁ Cloudinary
                        </button>
                        <button className={`btn ${activeSource === 'firebase' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => handleSourceChange('firebase')}>
                            🔥 Firebase Storage
                        </button>
                        <button className={`btn ${activeSource === 'auto' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => handleSourceChange('auto')}>
                            🔄 تلقائي (Fallback)
                        </button>
                    </div>
                    <small className="text-muted d-block mt-2">
                        {activeSource === 'auto' && 'سيتم الرفع لجميع المصادر المفعلة، وعند العرض يتم التبديل تلقائياً في حالة فشل المصدر الأساسي'}
                        {activeSource === 'cloudinary' && 'الرفع والعرض من Cloudinary — مع نسخ احتياطي تلقائي للمصادر الأخرى'}
                        {activeSource === 'firebase' && 'الرفع والعرض من Firebase Storage — مع نسخ احتياطي تلقائي للمصادر الأخرى'}
                    </small>
                </div>
            </div>

            {/* حسابات Cloudinary */}
            <div className="card shadow-sm mb-4">
                <div className="card-body">
                    <h5 className="card-title mb-3">☁ حسابات Cloudinary</h5>
                    {cloudinaryAccounts.length > 0 && (
                        <div className="list-group mb-3">
                            {cloudinaryAccounts.map(acc => (
                                <div key={acc.id} className={`list-group-item d-flex justify-content-between align-items-center ${!acc.enabled ? 'opacity-50' : ''} ${activeCloudinary === acc.id ? 'list-group-item-primary' : ''}`}>
                                    <div>
                                        <strong>{acc.label}</strong>
                                        {activeCloudinary === acc.id && <span className="badge bg-primary ms-2">نشط</span>}
                                        {acc.enabled ? <span className="badge bg-success ms-1">مفعل</span> : <span className="badge bg-secondary ms-1">معطل</span>}
                                        <small className="d-block text-muted" dir="ltr">{acc.cloudName} / {acc.preset}</small>
                                    </div>
                                    <div className="btn-group btn-group-sm">
                                        <button className={`btn ${acc.enabled ? 'btn-outline-warning' : 'btn-outline-success'}`}
                                            onClick={() => handleToggleEnabled('cloudinary', acc.id)}>
                                            {acc.enabled ? 'تعطيل' : 'تفعيل'}
                                        </button>
                                        {activeCloudinary !== acc.id && acc.enabled && (
                                            <button className="btn btn-outline-primary" onClick={() => handleActivateAccount('cloudinary', acc.id)}>تعيين نشط</button>
                                        )}
                                        <button className="btn btn-outline-danger" onClick={() => handleDeleteAccount('cloudinary', acc.id)}>حذف</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {cloudinaryAccounts.length === 0 && <p className="text-muted mb-3">لا توجد حسابات مضافة</p>}
                    <div className="row g-2">
                        <div className="col-md-3">
                            <input type="text" className="form-control form-control-sm" placeholder="اسم الحساب" value={newCloudLabel} onChange={e => setNewCloudLabel(e.target.value)} />
                        </div>
                        <div className="col-md-3">
                            <input type="text" className="form-control form-control-sm" placeholder="Cloud Name" value={newCloudName} onChange={e => setNewCloudName(e.target.value)} dir="ltr" />
                        </div>
                        <div className="col-md-3">
                            <input type="text" className="form-control form-control-sm" placeholder="Upload Preset" value={newCloudPreset} onChange={e => setNewCloudPreset(e.target.value)} dir="ltr" />
                        </div>
                        <div className="col-md-3">
                            <button className="btn btn-sm btn-success w-100" onClick={handleAddCloudinary}>➕ إضافة حساب</button>
                        </div>
                    </div>
                </div>
            </div>

            {/* حسابات Firebase */}
            <div className="card shadow-sm mb-4">
                <div className="card-body">
                    <h5 className="card-title mb-3">🔥 حسابات Firebase Storage</h5>
                    {firebaseAccounts.length > 0 && (
                        <div className="list-group mb-3">
                            {firebaseAccounts.map(acc => (
                                <div key={acc.id} className={`list-group-item d-flex justify-content-between align-items-center ${!acc.enabled ? 'opacity-50' : ''} ${activeFirebase === acc.id ? 'list-group-item-warning' : ''}`}>
                                    <div>
                                        <strong>{acc.label}</strong>
                                        {activeFirebase === acc.id && <span className="badge bg-warning text-dark ms-2">نشط</span>}
                                        {acc.enabled ? <span className="badge bg-success ms-1">مفعل</span> : <span className="badge bg-secondary ms-1">معطل</span>}
                                        <small className="d-block text-muted" dir="ltr">{acc.config?.projectId} / {acc.config?.storageBucket}</small>
                                    </div>
                                    <div className="btn-group btn-group-sm">
                                        <button className={`btn ${acc.enabled ? 'btn-outline-warning' : 'btn-outline-success'}`}
                                            onClick={() => handleToggleEnabled('firebase', acc.id)}>
                                            {acc.enabled ? 'تعطيل' : 'تفعيل'}
                                        </button>
                                        {activeFirebase !== acc.id && acc.enabled && (
                                            <button className="btn btn-outline-primary" onClick={() => handleActivateAccount('firebase', acc.id)}>تعيين نشط</button>
                                        )}
                                        <button className="btn btn-outline-danger" onClick={() => handleDeleteAccount('firebase', acc.id)}>حذف</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {firebaseAccounts.length === 0 && <p className="text-muted mb-3">لا توجد حسابات مضافة</p>}
                    <div className="row g-2">
                        <div className="col-md-3">
                            <input type="text" className="form-control form-control-sm" placeholder="اسم الحساب" value={newFbLabel} onChange={e => setNewFbLabel(e.target.value)} />
                        </div>
                        <div className="col-md-9">
                            <div className="input-group input-group-sm">
                                <input type="text" className="form-control" placeholder='{"apiKey":"...","projectId":"...","storageBucket":"..."}' value={newFbConfig} onChange={e => setNewFbConfig(e.target.value)} dir="ltr" />
                                <button className="btn btn-success" onClick={handleAddFirebase}>➕ إضافة</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SourceManager;
